import React, { useState, useEffect } from 'react';
import TempMail from './components/TempMail';
import Pricing from './components/Pricing';

const API_URL = 'https://tormal-api.winter-mouse-2fc0.workers.dev';

function App() {
  const [page, setPage] = useState('home');
  const [prices, setPrices] = useState({});

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPrices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/prices`);
      const data = await res.json();
      setPrices(data.prices || {});
    } catch (e) {
      console.error('Failed to fetch prices');
    }
  };

  const renderPage = () => {
    switch(page) {
      case 'tempmail': return <TempMail />;
      case 'pricing': return <Pricing prices={prices} />;
      default: return <Home setPage={setPage} prices={prices} />;
    }
  };

  return (
    <div style={styles.app}>
      <nav style={styles.nav}>
        <div style={styles.navContent}>
          <h1 style={styles.logo} onClick={() => setPage('home')}>TOR MAL</h1>
          <div style={styles.navLinks}>
            <button style={styles.navLink} onClick={() => setPage('home')}>Home</button>
            <button style={styles.navLink} onClick={() => setPage('tempmail')}>TempMail</button>
            <button style={styles.navLink} onClick={() => setPage('pricing')}>Pricing</button>
          </div>
          {prices.bitcoin && (
            <div style={styles.priceTicker}>
              <span style={{color: '#f7931a'}}>BTC</span> ${prices.bitcoin.usd?.toLocaleString()}
            </div>
          )}
        </div>
      </nav>
      <main style={styles.main}>
        {renderPage()}
      </main>
      <footer style={styles.footer}>
        <p>&copy; 2026 TOR MAL. Anonymous email for everyone.</p>
      </footer>
    </div>
  );
}

function Home({ setPage, prices }) {
  return (
    <div style={styles.home}>
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>Anonymous Email</h1>
        <p style={styles.heroSubtitle}>Secure. Private. Yours.</p>
        <p style={styles.heroDescription}>
          TOR MAL provides disposable temporary email addresses and permanent anonymous email accounts.
          Protect your privacy with military-grade encryption.
        </p>
        <div style={styles.ctaButtons}>
          <button style={styles.ctaPrimary} onClick={() => setPage('tempmail')}>
            Get Temp Email
          </button>
          <button style={styles.ctaSecondary} onClick={() => setPage('pricing')}>
            View Pricing
          </button>
        </div>
      </div>

      <div style={styles.features}>
        <h2 style={styles.sectionTitle}>Why TOR MAL?</h2>
        <div style={styles.featureGrid}>
          <div style={styles.featureCard}>
            <h3>🔒 End-to-End Encryption</h3>
            <p>Your emails are encrypted and can only be read by you</p>
          </div>
          <div style={styles.featureCard}>
            <h3>👤 100% Anonymous</h3>
            <p>No personal information required. No logs kept.</p>
          </div>
          <div style={styles.featureCard}>
            <h3>⚡ Instant Setup</h3>
            <p>Get a temporary email address in seconds</p>
          </div>
          <div style={styles.featureCard}>
            <h3>💰 Pay with Crypto</h3>
            <p>Bitcoin, Ethereum, Solana accepted</p>
          </div>
        </div>
      </div>

      <div style={styles.stats}>
        <div style={styles.statItem}>
          <div style={styles.statNumber}>50K+</div>
          <div>Active Users</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statNumber}>1M+</div>
          <div>Emails Protected</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statNumber}>0</div>
          <div>Data Breaches</div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  app: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  nav: {
    background: 'rgba(0,0,0,0.3)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '1rem 0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #00d4ff, #7b2cbf)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    cursor: 'pointer',
    margin: 0,
  },
  navLinks: {
    display: 'flex',
    gap: '2rem',
  },
  navLink: {
    background: 'none',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    transition: 'background 0.3s',
  },
  priceTicker: {
    background: 'rgba(255,255,255,0.1)',
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    fontSize: '0.9rem',
    fontFamily: 'monospace',
  },
  main: {
    flex: 1,
  },
  home: {
    padding: '2rem',
  },
  hero: {
    textAlign: 'center',
    padding: '6rem 2rem',
    maxWidth: '800px',
    margin: '0 auto',
  },
  heroTitle: {
    fontSize: '4rem',
    marginBottom: '1rem',
    background: 'linear-gradient(135deg, #00d4ff, #7b2cbf)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  heroSubtitle: {
    fontSize: '1.5rem',
    color: '#888',
    marginBottom: '2rem',
  },
  heroDescription: {
    fontSize: '1.2rem',
    color: '#aaa',
    marginBottom: '3rem',
    lineHeight: 1.6,
  },
  ctaButtons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  ctaPrimary: {
    padding: '1rem 2.5rem',
    fontSize: '1.2rem',
    background: 'linear-gradient(135deg, #00d4ff, #7b2cbf)',
    color: '#fff',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'transform 0.3s',
  },
  ctaSecondary: {
    padding: '1rem 2.5rem',
    fontSize: '1.2rem',
    background: 'transparent',
    color: '#fff',
    border: '2px solid rgba(255,255,255,0.3)',
    borderRadius: '50px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'all 0.3s',
  },
  features: {
    maxWidth: '1200px',
    margin: '4rem auto',
    padding: '0 2rem',
  },
  sectionTitle: {
    textAlign: 'center',
    fontSize: '2.5rem',
    marginBottom: '3rem',
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '2rem',
  },
  featureCard: {
    background: 'rgba(255,255,255,0.05)',
    padding: '2rem',
    borderRadius: '16px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '4rem',
    margin: '4rem 0',
    flexWrap: 'wrap',
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: '#00d4ff',
  },
  footer: {
    background: 'rgba(0,0,0,0.3)',
    padding: '2rem',
    textAlign: 'center',
    color: '#888',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
};

export default App;

